import { useNavigate } from "@tanstack/react-router";
import { api, endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { Booth, type QueuedSong } from "./Booth.js";
import { Failed, Loading } from "./Loading.js";

/**
 * The rate loop, reading the real queue.
 *
 * The queue is fetched ONCE per sitting and then owned by the machine. Refetching as ratings land
 * would pull songs out from under the cursor mid-sitting, and the whole design of the loop is that
 * nothing in the input path waits for or reacts to the network.
 */
export function BoothScreen({ strategy = "oldest" }: { strategy?: "oldest" | "most-played" } = {}) {
  const navigate = useNavigate();
  // The strategy is part of the fetch, so changing it starts a new sitting rather than reordering
  // one already in progress under the cursor.
  const queue = useResource(() => endpoints.queue(strategy, 500), [strategy]);
  // Only for the empty state; the booth never shows it during a sitting.
  const triage = useResource(() => api.get<{ attention: number }>("/api/triage?limit=1"), []);

  if (queue.error) return <Failed error={queue.error} retry={queue.reload} />;
  if (queue.loading) return <Loading what="the queue" />;

  const songs: QueuedSong[] = (queue.data?.songs ?? []).map((s) => ({
    id: s.id,
    title: s.title,
    artist: s.artist,
    album: s.album,
    albumId: s.albumId,
    year: s.year,
    genre: s.genre,
    durationMs: s.durationMs,
    skipCount: s.skipCount,
    art: s.art,
    rating: s.rating,
    ratedAt: s.ratedAt,
  }));

  // Booth renders its own empty state, which is a designed screen rather than a blank one.
  return (
    <Booth
      songs={songs}
      onExit={() => void navigate({ to: "/rankings" })}
      pending={triage.data?.attention ?? 0}
      onTriage={() => void navigate({ to: "/triage" })}
    />
  );
}
